import React from "react";
import { FiUpload, FiTrash2, FiDownload } from "react-icons/fi";
import { BsFillPlusCircleFill } from "react-icons/bs";

import useFirebaseFileDownload from "../../hooks/useFirebaseDownload";

const getFileTypeFromUrl = (url) => {
  try {
    const urlPath = new URL(url).pathname.toLowerCase();
    if (urlPath.includes('.pdf')) return 'pdf';
    if (urlPath.includes('.png') || urlPath.includes('.jpg') || urlPath.includes('.jpeg')) return 'image';
    if (urlPath.includes('.doc') || urlPath.includes('.docx')) return 'document';
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'pdf':
      return (
        <div className="flex flex-col items-center w-full h-full text-red-600 bg-red-50 pt-2 md:pt-3 lg:pt-8">
          <svg className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
          <span className="text-[10px] sm:text-sm font-medium mt-1">PDF</span>
        </div>
      );
    case 'document':
      return (
        <div className="flex flex-col items-center w-full h-full text-blue-600 bg-blue-50 pt-2 md:pt-3 lg:pt-8">
          <svg className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
          <span className="text-[10px] sm:text-sm font-medium mt-1">DOC</span>
        </div>
      );
    default:
      return (
        <div className="flex flex-col items-center w-full h-full text-gray-600 bg-gray-50 pt-2 md:pt-3 lg:pt-8">
          <svg className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
          <span className="text-[10px] sm:text-sm font-medium mt-1">FILE</span>
        </div>
      );
  }
};

const FileUploadInput = ({
  fileInputRef,
  onFileChange,
  files,
  onRemoveFile,
  existingsFiles,
  onValidationError
}) => {

  const { downloadFileFromFirebase, isDownloading } = useFirebaseFileDownload();

  const isImage = (file) => file.type.startsWith("image/");
  const isPDF = (file) => file.type === "application/pdf";
  const isURL = (file) => {
    try {
      new URL(file);
      return true;
    } catch (e) {
      return false;
    }
  };

  // File validation function
  const isValidFileType = (file) => {
    const allowedTypes = [
      'image/png',
      'image/jpg',
      'image/jpeg',
      'application/pdf'
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const isValidFileSize = (file, maxSizeMB = 5) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024; // Convert MB to bytes
    return file.size <= maxSizeBytes;
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach(file => {
      // Check file type
      if (!isValidFileType(file)) {
        invalidFiles.push({
          name: file.name,
          reason: `Unsupported file type. Only PNG, JPG, JPEG, and PDF files are allowed.`
        });
        return;
      }

      // Check file size
      if (!isValidFileSize(file)) {
        invalidFiles.push({
          name: file.name,
          reason: `File size too large. Maximum size is 5MB.`
        });
        return;
      }

      validFiles.push(file);
    });

    // Show validation errors if any
    if (invalidFiles.length > 0 && onValidationError) {
      onValidationError(invalidFiles);
    }

    // Only pass valid files to parent component
    if (validFiles.length > 0) {
      // Create a synthetic event object for the valid files
      const syntheticEvent = {
        target: {
          files: validFiles
        }
      };
      onFileChange(syntheticEvent);
    }
  };

  const handleDownload = async (url, index) => {
    try {
      // Extract filename from URL for better naming
      const urlPath = new URL(url).pathname;
      let fileName = urlPath.split('/').pop();

      if (fileName) {
        // Decode URL-encoded filename
        fileName = decodeURIComponent(fileName);
        // Remove timestamp prefix if present (from upload function)
        fileName = fileName.replace(/^\d+/, '');
      } else {
        fileName = `document-${index + 1}`;
      }

      await downloadFileFromFirebase(url, fileName);

    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div>
      <label className="text-[#09114B] text-[11px] sm:text-[13px] md:text-[15px] lg:text-[17px] font-inter font-medium mb-2 block">
        Support Documents (Optional)
      </label>
      <div
        className="
          relative
          flex flex-wrap w-full md:h- lg:h-[234px] p-[8px] sm:p-[12px] md:p-[16px] lg:p-[20px] 
          gap-4 sm:gap-5 md:gap-6 lg:gap-7 justify-start
          rounded-[5px]
          border border-[rgba(129,129,129,0.5)]
          text-center
          bg-[#F7F9FF] 
          overflow-auto
        "
        onClick={() => {
          if (files.length === 0 && existingsFiles.length === 0) {
            fileInputRef.current.click();
          }
        }}
      >
        {files.length > 0 || existingsFiles.length > 0 ? (
          <>
            {/* Render existing files (URLs) first, if any */}
            {existingsFiles.map((urlfile, index) => {
              const fileType = getFileTypeFromUrl(urlfile);

              return (
                <div
                  key={`existing-${index}`}
                  className="relative w-[92px] h-[92px] sm:w-[122px] sm:h-[122px] md:w-[152px] md:h-[152px] lg:w-[192px] lg:h-[192px] rounded-[8px] overflow-hidden border border-gray-300 bg-gray-50 flex items-center justify-center"
                >
                  {fileType === 'image' ? (
                    <img
                      src={urlfile}
                      alt="preview"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    getFileIcon(fileType)
                  )}

                  {/* Action buttons container */}
                  <div className="absolute inset-x-0 bottom-2 flex justify-center gap-2 sm:gap-4">
                    {/* Download button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(urlfile, index);
                      }}
                      disabled={isDownloading}
                      className={`p-1 bg-white rounded-[6px] shadow transition-colors ${isDownloading
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-blue-50'
                        }`}
                      title="Download"
                      type="button"
                    >
                      <FiDownload className={`text-[#2563EB] bg-[#FEF2F2] w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-[6px] ${isDownloading ? 'animate-pulse' : ''
                        }`} />
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFile(index, true);
                      }}
                      className="p-1 bg-white rounded-[6px] shadow hover:bg-red-50"
                      title="Remove"
                      type="button"
                    >
                      <FiTrash2 className="text-[#EF4444] bg-[#FEF2F2] w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-[6px]" />
                    </button>
                  </div>
                </div>
              )
            })}

            {/* Render newly uploaded files */}
            {files.map((file, index) => (
              <div
                key={`new-${index}`}
                className="relative w-[92px] h-[92px] sm:w-[122px] sm:h-[122px] md:w-[152px] md:h-[152px] lg:w-[192px] lg:h-[192px] rounded-[8px] overflow-hidden border border-gray-300 bg-gray-50 flex items-center justify-center"
              >
                {isImage(file) ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="object-cover w-full h-full"
                  />
                ) : isPDF(file) ? (
                  <div className="flex items-center justify-center w-full h-full text-sm text-gray-600">
                    PDF
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-sm text-gray-400">
                    File
                  </div>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                  className="absolute top-[60px] sm:top-[90px] md:top-[112px] lg:top-[145px] lg:right-20 p-1 bg-white rounded-[6px] shadow hover:bg-red-50"
                  title="Remove"
                  type="button"
                >
                  <FiTrash2 className="text-[#EF4444] bg-[#FEF2F2] w-3 h-3 sm:w-4 sm:h-4  md:w-6 md:h-6 lg:w-8 lg:h-8 rounded-[6px]" />
                </button>
              </div>
            ))}
            <div
              className="w-[92px] h-[92px] sm:w-[122px] sm:h-[122px] md:w-[152px] md:h-[152px] lg:w-[192px] lg:h-[192px] rounded-[8px] border border-dashed border-[#D4D4D4] flex items-center justify-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
            >
              <BsFillPlusCircleFill className="text-[#A3A3A3] h-8 w-8" />
            </div>
          </>
        ) : (
          <div 
            className="flex flex-col justify-center items-center gap-[14px] w-full font-[400] text-center text-gray-500 cursor-pointer"
            onClick={() => fileInputRef.current.click()}
          >
            <FiUpload className="w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 lg:w-[55.33px] lg:h-[55.33px] text-[#B1B1B1]" />
            <p className="text-[#707070] text-[11px] sm:text-[12px] md:text-[13px] lg:text-[14px]">
              Select your file here
            </p>
            <p className="text-[8px] sm:text-[9px] md:text-[10px] lg:text-[11px] text-[#878787]">
              JPG, PNG or PDF, file size 5 MB
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
              className="
                flex h-6 sm:h-[26px] md:h-7 lg:h-[30px] py-2 lg:py-[9.058px] px-[12.078px] text-[8px] sm:text-[10px] md:text-[11px]  lg:text-[12px]
                items-center gap-[7.549px]
                rounded-[3.774px]
                border border-[#873FE2]
                bg-[#873FE2] text-white
                font-normal cursor-pointer
                mx-auto
              "
            >
              BROWSE
            </button>
          </div>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept=".png,.jpg,.jpeg,.pdf"
        className="hidden"
      />
    </div>
  );
};

export default FileUploadInput;
